import { useState, useRef, useEffect } from "react";
import { select, pointer } from "d3-selection";
import { axisBottom, axisLeft } from "d3-axis";
import { scaleLinear } from "d3-scale";
import { zoom, zoomIdentity } from "d3-zoom";
import { line, area } from "d3-shape";
import { HeaderWithModal, Modal } from "../utils.jsx";
import { useRibocrypt, useRibocryptDetails, useFetchProtDetails } from "../hooks.js";

const margin = {top: 20, right: 30, bottom: 40, left: 55};
const width = 1000 - margin.left - margin.right;
const plotHeight = 160;
const xAxisY = margin.top + plotHeight;
const transcriptY = xAxisY + 62;   // transcript backbone / exon boxes
const orfY = transcriptY + 18;     // ORF row, sitting below the transcript
const exonHeight = 7;
const orfHeight = 16;
const frameTrackTop = orfY + 16;   // top of the three-frame codon track
const frameBandHeight = 10;        // height of each of the three frame bands
const frameBandGap = 1;            // vertical gap between frame bands
const frameTrackHeight = 3 * frameBandHeight + 2 * frameBandGap;
const mappabilityTop = frameTrackTop + frameTrackHeight + 24;   // top of mappability track
const mappabilityHeight = 24;                                    // band height
const translonTrackTop = mappabilityTop + mappabilityHeight + 24;   // top of predicted-translon track
const translonBandHeight = 10;      // height of each of the three translon frame lanes
const translonBandGap = 1;          // vertical gap between translon lanes
const translonTrackHeight = 3 * translonBandHeight + 2 * translonBandGap;
const totalHeight = translonTrackTop + translonTrackHeight + 10;

// Reading-frame colors: frame 0 green, frame 1 red, frame 2 blue.
const FRAME_COLORS = ["#16a34a", "#dc2626", "#2563eb"];
const START_CODONS = new Set(["ATG"]);
const STOP_CODONS = new Set(["TAA", "TAG", "TGA"]);

// Hide (but keep) the splicing-debug modal: the button and modal are not
// rendered while this is false; flip to true to re-enable the debug UI.
const SHOW_DEBUG_MODAL = false;

// ---------------------------------------------------------------------------
// Translation helpers (shared by the plot and the splicing-debug modal)
// ---------------------------------------------------------------------------

// Standard genetic code (DNA codons -> one-letter amino acids; '*' = stop).
const CODON_TABLE = {
    TTT: "F", TTC: "F", TTA: "L", TTG: "L", CTT: "L", CTC: "L", CTA: "L", CTG: "L",
    ATT: "I", ATC: "I", ATA: "I", ATG: "M", GTT: "V", GTC: "V", GTA: "V", GTG: "V",
    TCT: "S", TCC: "S", TCA: "S", TCG: "S", CCT: "P", CCC: "P", CCA: "P", CCG: "P",
    ACT: "T", ACC: "T", ACA: "T", ACG: "T", GCT: "A", GCC: "A", GCA: "A", GCG: "A",
    TAT: "Y", TAC: "Y", TAA: "*", TAG: "*", CAT: "H", CAC: "H", CAA: "Q", CAG: "Q",
    AAT: "N", AAC: "N", AAA: "K", AAG: "K", GAT: "D", GAC: "D", GAA: "E", GAG: "E",
    TGT: "C", TGC: "C", TGA: "*", TGG: "W", CGT: "R", CGC: "R", CGA: "R", CGG: "R",
    AGT: "S", AGC: "S", AGA: "R", AGG: "R", GGT: "G", GGC: "G", GGA: "G", GGG: "G"
};

const COMPLEMENT = {A: "T", T: "A", G: "C", C: "G", N: "N"};

const reverseComplement = seq =>
    seq.split("").reverse().map(b => COMPLEMENT[b] || "N").join("");

// Normalize an RNA/DNA sequence for translation (uppercase, U->T).
const normalizeSeq = s => (typeof s === "string" ? s.toUpperCase().replace(/U/g, "T") : null);

// Translate a nucleotide sequence starting at `frame` (0/1/2) to the stop or
// the end of the sequence.
const translateFrame = (seq, frame) => {
    let aa = "";
    for (let i = frame; i + 3 <= seq.length; i += 3) {
        aa += CODON_TABLE[seq.slice(i, i + 3)] || "X";
    }
    return aa;
};

// Try to locate the exact protein sequence within a translated frame. Also
// tries the protein without its leading residue (alt/non-AUG starts often
// leave the initiator differing). Returns match info or null.
const findProtein = (peptide, protein) => {
    if (!peptide || !protein) return null;
    let idx = peptide.indexOf(protein);
    if (idx >= 0) return {aaOffset: idx, mode: "exact"};
    if (protein.length > 1) {
        idx = peptide.indexOf(protein.slice(1));
        if (idx >= 0) return {aaOffset: idx - 1, mode: "minus-initiator"};
    }
    return null;
};

// Locate the ORF for `protein` inside the forward transcript sequence by
// translating all three frames and matching the protein. Returns transcript
// (nt) coordinates {start, end} where [start, end) is the coding region and
// `end` is the first base of the stop codon — or null if not found on the
// forward strand. This anchors the ORF diagram to rna_seq (the authoritative
// spliced sequence), which the ribocrypt coverage/segments can disagree with.
const findOrfInSequence = (seq, protein) => {
    if (!seq || !protein) return null;
    for (let f = 0; f < 3; f++) {
        const hit = findProtein(translateFrame(seq, f), protein);
        if (hit) {
            const start = f + hit.aaOffset * 3;
            return {start, end: start + protein.length * 3, mode: hit.mode};
        }
    }
    return null;
};

// Parse a segments column ("[[start,end],...]" genomic coordinates) into an
// array of [start, end] pairs. Returns null when unavailable/invalid.
const parseSegments = s => {
    if (!s) return null;
    try {
        const parsed = typeof s === "string" ? JSON.parse(s) : s;
        return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
    } catch {
        return null;
    }
};

// Map a genomic coordinate to a transcript (spliced) coordinate, mirroring how
// the ribocrypt_details coverage array is built server-side: exons are walked
// in list order and, on the minus strand, each exon [s,e) is read 3'→5' so its
// last genomic base (e-1) is the first transcript position of that exon.
const genomicToTranscript = (g, exons, strand) => {
    let offset = 0;
    for (const [s, e] of exons) {
        const len = e - s;
        if (g >= s && g < e) {
            return strand === "-" ? offset + (e - 1 - g) : offset + (g - s);
        }
        offset += len;
    }
    return null;
};

// Ribo-seq footprint (RFP) coverage plot with a transcript/ORF track below.
// The coverage is split into the three absolute reading frames (position mod 3)
// and drawn as three curves: frame 0 green, frame 1 red, frame 2 blue. The ORF
// box, codon marks and frame bands are anchored to rna_seq (the authoritative
// spliced sequence): the ORF is located by matching the protein, so its start
// lands on a start codon and its end on a stop codon in the same frame. The
// coverage/exon boxes are rebuilt from genomic segments and can disagree with
// rna_seq by a few nt, so they are best-effort on the shared x-axis.
const RibocryptCoveragePlot = ({coverage, mappability, translons, transcriptSegments, protSegments, strand, transcriptLabel, orfLabel, transcriptSequence, proteinSequence}) => {
    const svgRef = useRef();
    const [currentZoomState, setCurrentZoomState] = useState();
    const [hover, setHover] = useState(null);
    const [mapHover, setMapHover] = useState(null);

    useEffect(() => {
        if (!svgRef.current || !Array.isArray(coverage) || coverage.length === 0) {
            return;
        }

        // Coverage values can contain NaN (positions with no data): treat as 0.
        const values = coverage.map(v => (Number.isFinite(v) ? v : 0));
        const maxValue = Math.max(1, ...values);

        // rna_seq is the authoritative spliced transcript sequence. Everything
        // in the transcript/ORF/frame tracks is anchored to it. Note it can be
        // longer/shorter than the coverage array (which is rebuilt from the
        // genomic segments and may disagree by a few nt), so the coverage/exon
        // boxes are best-effort while the ORF box and codon marks are exact.
        const seq = normalizeSeq(transcriptSequence);
        const seqLen = seq ? seq.length : 0;

        // Transcript exons -> cumulative spans in transcript coordinates.
        const exons = parseSegments(transcriptSegments);
        const orfSegs = parseSegments(protSegments);

        let exonSpans = [];
        let transcriptLen = values.length;
        if (exons) {
            let offset = 0;
            exonSpans = exons.map(([s, e]) => {
                const span = [offset, offset + (e - s)];
                offset += (e - s);
                return span;
            });
            transcriptLen = Math.max(offset, values.length);
        }

        // Locate the ORF in rna_seq coordinates by matching the protein. This
        // is exact (start codon at start, stop codon at end) and robust to
        // non-AUG starts. Fall back to the genomic-segment mapping only when
        // the sequence or protein is unavailable.
        let orfRange = null;
        const orfFromSeq = findOrfInSequence(seq, normalizeSeq(proteinSequence));
        if (orfFromSeq) {
            orfRange = [orfFromSeq.start, orfFromSeq.end];
        } else if (exons && orfSegs) {
            const coords = [];
            for (const [s, e] of orfSegs) {
                const a = genomicToTranscript(s, exons, strand);
                const b = genomicToTranscript(e - 1, exons, strand);
                if (a !== null) coords.push(a);
                if (b !== null) coords.push(b);
            }
            if (coords.length > 0) {
                orfRange = [Math.min(...coords), Math.max(...coords) + 1];
            }
        }

        const svg = select(svgRef.current);

        const domainMax = Math.max(values.length - 1, transcriptLen, seqLen - 1);
        const xScale = scaleLinear()
            .domain([0, domainMax])
            .range([margin.left, margin.left + width]);

        if (currentZoomState) {
            const newXScale = currentZoomState.rescaleX(xScale);
            xScale.domain(newXScale.domain());
        }

        const yScale = scaleLinear()
            .domain([0, maxValue])
            .range([xAxisY, margin.top]);

        const xAxis = axisBottom(xScale);
        const yAxis = axisLeft(yScale)
            .tickFormat(d => d >= 10000 ? `${d / 1000}k` : d);
        svg.select(".x-axis")
            .style("transform", `translateY(${xAxisY}px)`)
            .call(xAxis);
        svg.select(".y-axis")
            .style("transform", `translateX(${margin.left}px)`)
            .call(yAxis);

        // Split the signal into the three reading frames. Frames are absolute
        // (transcript position mod 3), so the bands/curves and the ORF box all
        // share one consistent definition and the ORF falls into a real frame.
        const frameOrigin = 0;
        const frameOf = i => (((i - frameOrigin) % 3) + 3) % 3;
        const framePoints = [[], [], []];
        values.forEach((v, i) => framePoints[frameOf(i)].push({i, v}));

        const lineGenerator = line()
            .x(d => xScale(d.i))
            .y(d => yScale(d.v));

        const areaGenerator = area()
            .x(d => xScale(d.i))
            .y0(yScale(0))
            .y1(d => yScale(d.v));

        svg.selectAll(".frame-area")
            .data(framePoints)
            .join("path")
            .attr("class", "frame-area")
            .attr("fill", (d, i) => FRAME_COLORS[i])
            .attr("fill-opacity", 0.2)
            .attr("stroke", "none")
            .attr("clip-path", "url(#ribocrypt-clip)")
            .attr("d", d => areaGenerator(d));

        svg.selectAll(".frame-curve")
            .data(framePoints)
            .join("path")
            .attr("class", "frame-curve")
            .attr("fill", "none")
            .attr("stroke", (d, i) => FRAME_COLORS[i])
            .attr("stroke-width", 1)
            .attr("clip-path", "url(#ribocrypt-clip)")
            .attr("d", d => lineGenerator(d));

        svg.selectAll(".y-axis-label")
            .data(["RFP"])
            .join("text")
            .attr("class", "y-axis-label")
            .attr("text-anchor", "middle")
            .attr("fill", "#334155")
            .style("font-size", "12px")
            .attr("x", -(margin.top + plotHeight / 2))
            .attr("y", 14)
            .attr("transform", "rotate(-90)")
            .text(d => d);

        svg.selectAll(".x-axis-label")
            .data(["Transcript position (nt)"])
            .join("text")
            .attr("class", "x-axis-label")
            .attr("text-anchor", "middle")
            .attr("fill", "#334155")
            .style("font-size", "12px")
            .attr("x", margin.left + width / 2)
            .attr("y", xAxisY + 36)
            .text(d => d);

        svg.selectAll(".zoom-pan-hint")
            .data(["scroll to zoom, drag to pan"])
            .join("text")
            .attr("class", "zoom-pan-hint")
            .attr("text-anchor", "start")
            .attr("fill", "#94a3b8")
            .style("font-size", "11px")
            .attr("x", margin.left)
            .attr("y", xAxisY + 36)
            .text(d => d);

        // ---- Transcript / ORF track ----------------------------------------
        // Backbone line spanning the full transcript.
        svg.selectAll(".transcript-backbone")
            .data(exonSpans.length > 0 ? [[0, transcriptLen]] : [])
            .join("line")
            .attr("class", "transcript-backbone")
            .attr("clip-path", "url(#ribocrypt-clip)")
            .attr("x1", d => xScale(d[0]))
            .attr("x2", d => xScale(d[1]))
            .attr("y1", transcriptY)
            .attr("y2", transcriptY)
            .attr("stroke", "#94a3b8")
            .attr("stroke-width", 1);

        svg.selectAll(".exon-box")
            .data(exonSpans)
            .join("rect")
            .attr("class", "exon-box")
            .attr("clip-path", "url(#ribocrypt-clip)")
            .attr("x", d => xScale(d[0]))
            .attr("width", d => Math.max(1, xScale(d[1]) - xScale(d[0])))
            .attr("y", transcriptY - exonHeight / 2)
            .attr("height", exonHeight)
            .attr("fill", "#64748b");

        svg.selectAll(".orf-box")
            .data(orfRange ? [orfRange] : [])
            .join("rect")
            .attr("class", "orf-box")
            .attr("clip-path", "url(#ribocrypt-clip)")
            .attr("x", d => xScale(d[0]))
            .attr("width", d => Math.max(1, xScale(d[1]) - xScale(d[0])))
            .attr("y", orfY - orfHeight / 2)
            .attr("height", orfHeight)
            .attr("fill", d => FRAME_COLORS[frameOf(d[0])])
            .attr("fill-opacity", 0.85);

        // Protein accession of the displayed ORF, centered on top of the ORF box.
        svg.selectAll(".orf-label")
            .data(orfRange && orfLabel ? [orfRange] : [])
            .join("text")
            .attr("class", "orf-label")
            .attr("clip-path", "url(#ribocrypt-clip)")
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "central")
            .attr("fill", "#ffffff")
            .style("font-size", "12px")
            .attr("x", d => xScale((d[0] + d[1]) / 2))
            .attr("y", orfY)
            .text(orfLabel);

        svg.selectAll(".track-label")
            .data(exonSpans.length > 0 && transcriptLabel ? [transcriptLabel] : [])
            .join("text")
            .attr("class", "track-label")
            .attr("fill", "#334155")
            .style("font-size", "11px")
            .attr("x", margin.left)
            .attr("y", transcriptY - exonHeight / 2 - 8)
            .text(d => d);

        // ---- Three-frame codon track ---------------------------------------
        // Three stacked bands, one per absolute reading frame (transcript
        // position mod 3). Each band is filled with its frame color; within it,
        // start codons (ATG) are drawn as white vertical lines and stop codons
        // (TAA/TAG/TGA) as black vertical lines. Codon positions are read from
        // rna_seq, the same authoritative sequence the ORF box is anchored to.
        const bandTop = b => frameTrackTop + b * (frameBandHeight + frameBandGap);

        const frameBands = seq ? [0, 1, 2] : [];
        svg.selectAll(".frame-band")
            .data(frameBands)
            .join("rect")
            .attr("class", "frame-band")
            .attr("clip-path", "url(#ribocrypt-clip)")
            .attr("x", xScale(0))
            .attr("width", Math.max(1, xScale(Math.max(seq ? seq.length : 0, transcriptLen)) - xScale(0)))
            .attr("y", b => bandTop(b))
            .attr("height", frameBandHeight)
            .attr("fill", b => FRAME_COLORS[b])
            .attr("fill-opacity", 0.55);

        // Reading-frame numbers (1/2/3) in front of each band, in the frame color.
        svg.selectAll(".frame-number")
            .data(frameBands)
            .join("text")
            .attr("class", "frame-number")
            .attr("text-anchor", "end")
            .attr("dominant-baseline", "central")
            .attr("fill", b => FRAME_COLORS[b])
            .style("font-size", "11px")
            .attr("x", margin.left - 6)
            .attr("y", b => bandTop(b) + frameBandHeight / 2)
            .text(b => b + 1);

        // Collect codon marks for all three bands.
        const codonMarks = [];
        if (seq) {
            for (let b = 0; b < 3; b++) {
                const start = ((frameOrigin + b) % 3 + 3) % 3;
                for (let p = start; p + 3 <= seq.length; p += 3) {
                    const codon = seq.slice(p, p + 3);
                    if (START_CODONS.has(codon)) {
                        codonMarks.push({p, band: b, type: "start"});
                    } else if (STOP_CODONS.has(codon)) {
                        codonMarks.push({p, band: b, type: "stop"});
                    }
                }
            }
        }

        svg.selectAll(".codon-mark")
            .data(codonMarks)
            .join("line")
            .attr("class", "codon-mark")
            .attr("clip-path", "url(#ribocrypt-clip)")
            .attr("x1", d => xScale(d.p))
            .attr("x2", d => xScale(d.p))
            .attr("y1", d => bandTop(d.band))
            .attr("y2", d => bandTop(d.band) + frameBandHeight)
            .attr("stroke", d => (d.type === "start" ? "#ffffff" : "#000000"))
            .attr("stroke-width", 1);

        // Section title for the three-frame codon track, above it.
        svg.selectAll(".frame-track-label")
            .data(seq ? ["Frames"] : [])
            .join("text")
            .attr("class", "frame-track-label")
            .attr("text-anchor", "start")
            .attr("fill", "#334155")
            .style("font-size", "12px")
            .attr("x", 6)
            .attr("y", frameTrackTop - 6)
            .text(d => d);

        // ---- Mappability track ---------------------------------------------
        // Per-base mappability score (0..1) in transcript coordinates, from
        // rc/data/mappability/bigwig/{sn}-mappability.bw. Drawn as a short bar
        // chart: one vertical bar per nucleotide, height proportional to the
        // score, coloured by magnitude (blue = low, yellow = high). Bars are
        // batched per colour bucket into a handful of SVG paths so zooming
        // stays fast even for transcripts thousands of nt long.
        const mapVals = Array.isArray(mappability)
            ? mappability.map(v => (Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 0))
            : null;

        svg.selectAll(".mappability-band")
            .data(mapVals ? [mapVals] : [])
            .join("rect")
            .attr("class", "mappability-band")
            .attr("clip-path", "url(#ribocrypt-clip)")
            .attr("x", margin.left)
            .attr("width", width)
            .attr("y", mappabilityTop)
            .attr("height", mappabilityHeight)
            .attr("fill", "#f1f5f9")
            .attr("stroke", "#cbd5e1")
            .attr("stroke-width", 0.5);

        if (mapVals) {
            const baseW = xScale(1) - xScale(0);
            const barW = Math.max(0.5, baseW * 0.8);
            const barGap = Math.max(0, baseW - barW);
            const bottom = mappabilityTop + mappabilityHeight;

            // One path per colour bucket (v*BUCKETS). Each bar is a tiny
            // rect-subpath "M x y h w v h -w Z", which keeps the whole track at
            // <= BUCKETS DOM nodes regardless of transcript length.
            const BUCKETS = 20;
            const bucketPaths = Array.from({length: BUCKETS}, () => []);
            mapVals.forEach((v, i) => {
                if (v <= 0) return;
                const h = v * mappabilityHeight;
                const b = Math.min(BUCKETS - 1, Math.floor(v * BUCKETS));
                const x = xScale(i) + barGap / 2;
                const y = bottom - h;
                bucketPaths[b].push(`M${x} ${y}h${barW}v${h}h-${barW}Z`);
            });

            svg.selectAll(".mappability-bar")
                .data(bucketPaths.map((d, i) => ({i, d})).filter(o => o.d.length > 0))
                .join("path")
                .attr("class", "mappability-bar")
                .attr("clip-path", "url(#ribocrypt-clip)")
                .attr("fill", d => {
                    // Inverted: low values are pale/light, high values dark —
                    // dark reads as "high" and the tallest bars get the most
                    // prominent colour, so they don't look inverted.
                    const t = 1 - (d.i + 0.5) / BUCKETS; // 1..0 across buckets
                    const r = Math.round(68 + t * (253 - 68));
                    const g = Math.round(1 + t * (231 - 1));
                    const b = Math.round(84 + t * (37 - 84));
                    return `rgb(${r},${g},${b})`;
                })
                .attr("fill-opacity", 0.9)
                .attr("d", d => d.d.join(""));

            // Small y-axis for the mappability bars (score 0..1), drawn at the
            // left edge of the clip region and spanning only the track height.
            // Range is [bottom, top] so 0 sits at the baseline and 1.0 at the
            // top — matching the bars (tallest = 1.0).
            const mapYScale = scaleLinear()
                .domain([0, 1])
                .range([mappabilityTop + mappabilityHeight, mappabilityTop]);

            svg.select(".mappability-y-axis")
                .style("transform", `translateX(${margin.left}px)`)
                .call(axisLeft(mapYScale).ticks(2).tickSize(3));
        }

        svg.selectAll(".mappability-label")
            .data(mapVals ? ["Mappability"] : [])
            .join("text")
            .attr("class", "mappability-label")
            .attr("text-anchor", "start")
            .attr("fill", "#334155")
            .style("font-size", "12px")
            .attr("x", 6)
            .attr("y", mappabilityTop - 6)
            .text(d => d);

        // ---- Predicted-translon track --------------------------------------
        // Predicted translons (ORF predictions) from the pred_translons BED,
        // already mapped to transcript coordinates server-side. Drawn like the
        // three-frame codon track: three stacked lanes (one per absolute reading
        // frame, position mod 3), each translon a box in its frame's lane and
        // coloured by frame. Frame = transcript-start mod 3, the same convention
        // used by the ORF box and frame bands.
        const translonBandTop = b => translonTrackTop + b * (translonBandHeight + translonBandGap);
        const hasTranslons = Array.isArray(translons) && translons.length > 0;

        // Faint frame-coloured lane backgrounds spanning the transcript.
        svg.selectAll(".translon-lane")
            .data(hasTranslons ? [0, 1, 2] : [])
            .join("rect")
            .attr("class", "translon-lane")
            .attr("clip-path", "url(#ribocrypt-clip)")
            .attr("x", xScale(0))
            .attr("width", Math.max(1, xScale(Math.max(seq ? seq.length : 0, transcriptLen)) - xScale(0)))
            .attr("y", b => translonBandTop(b))
            .attr("height", translonBandHeight)
            .attr("fill", b => FRAME_COLORS[b])
            .attr("fill-opacity", 0.12);

        // Reading-frame numbers (1/2/3) to the left of each lane.
        svg.selectAll(".translon-frame-number")
            .data(hasTranslons ? [0, 1, 2] : [])
            .join("text")
            .attr("class", "translon-frame-number")
            .attr("text-anchor", "end")
            .attr("dominant-baseline", "central")
            .attr("fill", b => FRAME_COLORS[b])
            .style("font-size", "11px")
            .attr("x", margin.left - 6)
            .attr("y", b => translonBandTop(b) + translonBandHeight / 2)
            .text(b => b + 1);

        // One box per translon, placed in its frame's lane.
        const translonData = hasTranslons
            ? translons.map(t => ({...t, frame: frameOf(t.start)}))
            : [];
        svg.selectAll(".translon-box")
            .data(translonData)
            .join("rect")
            .attr("class", "translon-box")
            .attr("clip-path", "url(#ribocrypt-clip)")
            .attr("x", d => xScale(d.start))
            .attr("width", d => Math.max(1, xScale(d.end) - xScale(d.start)))
            .attr("y", d => translonBandTop(d.frame))
            .attr("height", translonBandHeight)
            .attr("fill", d => FRAME_COLORS[d.frame])
            .attr("fill-opacity", 0.85);

        svg.selectAll(".translon-label")
            .data(hasTranslons ? ["Translons"] : [])
            .join("text")
            .attr("class", "translon-label")
            .attr("text-anchor", "start")
            .attr("fill", "#334155")
            .style("font-size", "12px")
            .attr("x", 6)
            .attr("y", translonTrackTop - 6)
            .text(d => d);

        const zoomBehavior = zoom()
            .scaleExtent([1, 500])
            .translateExtent([[margin.left, 0], [margin.left + width, plotHeight]])
            .extent([[margin.left, 0], [margin.left + width, plotHeight]])
            .on("zoom", event => setCurrentZoomState(event.transform));

        svg.call(zoomBehavior);

        // On first render for this transcript (no user zoom yet), auto-fit the
        // view so the ORF is centered and fills most of the visible width.
        if (!currentZoomState && orfRange && domainMax > 0) {
            const R0 = margin.left;
            const orfLen = Math.max(1, orfRange[1] - orfRange[0]);
            const center = (orfRange[0] + orfRange[1]) / 2;
            // Let the ORF occupy ~70% of the visible width.
            let win = Math.min(orfLen / 0.7, domainMax);
            let dL = center - win / 2;
            let dR = center + win / 2;
            if (dL < 0) { dR -= dL; dL = 0; }
            if (dR > domainMax) { dL -= (dR - domainMax); dR = domainMax; }
            if (dL < 0) dL = 0;
            const k = Math.max(1, Math.min(500, domainMax / Math.max(1, dR - dL)));
            const sL = R0 + (dL / domainMax) * width;   // screen x of dL at k=1
            const tx = R0 - k * sL;
            svg.call(zoomBehavior.transform, zoomIdentity.translate(tx, 0).scale(k));
        }

        // ---- Coverage tooltip -------------------------------------------------
        // Transparent overlay over the plot area: convert the cursor to a
        // transcript position (xScale.invert), then report position, RFP and
        // absolute reading frame in a fixed-position HTML tooltip.
        svg.selectAll(".hover-overlay")
            .on("mousemove", event => {
                const [mx] = pointer(event, svgRef.current);
                const pos = Math.round(xScale.invert(mx));
                if (pos >= 0 && pos < values.length) {
                    setHover({
                        pos,
                        rfp: values[pos],
                        frame: frameOf(pos),
                        clientX: event.clientX,
                        clientY: event.clientY
                    });
                } else {
                    setHover(null);
                }
            })
            .on("mouseleave", () => setHover(null));

        // ---- Mappability tooltip ---------------------------------------------
        // Listener on the svg root (not an overlay rect) so it fires no matter
        // which D3-drawn element is under the cursor; gated to the mappability
        // band's vertical extent. Reports transcript position, the nucleotide at
        // that position, and the raw mappability score.
        const mapBandTop = mappabilityTop;
        const mapBandBottom = mappabilityTop + mappabilityHeight;
        svg.on("mousemove.map", event => {
            const [mx, my] = pointer(event, svgRef.current);
            if (mapVals && my >= mapBandTop && my <= mapBandBottom) {
                const pos = Math.round(xScale.invert(mx));
                if (pos >= 0 && pos < mapVals.length) {
                    setMapHover({
                        pos,
                        nt: seq && seq[pos] ? seq[pos] : "?",
                        value: mappability[pos],
                        clientX: event.clientX,
                        clientY: event.clientY
                    });
                } else {
                    setMapHover(null);
                }
            } else {
                setMapHover(null);
            }
        });
        svg.on("mouseleave.map", () => setMapHover(null));
    }, [coverage, mappability, translons, transcriptSegments, protSegments, strand, transcriptLabel, orfLabel, transcriptSequence, proteinSequence, currentZoomState]);

    return (
        <>
            <p className="text-xs text-slate-400 mt-2">
                Ribo-seq coverage data from <a href="https://ribocrypt.org/" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-600">RiboCrypt</a>.
            </p>
            <svg
                ref={svgRef}
                className="mt-1 w-full"
                viewBox={`0 0 ${width + margin.left + margin.right} ${totalHeight}`}
            >
                <defs>
                    <clipPath id="ribocrypt-clip">
                        <rect x={margin.left} y={0} width={width} height={totalHeight} />
                    </clipPath>
                </defs>
                <g className="x-axis" />
                <g className="y-axis" />
                <g className="mappability-y-axis" />
                <rect
                    className="hover-overlay"
                    x={margin.left}
                    y={margin.top}
                    width={width}
                    height={plotHeight}
                    fill="transparent"
                />
            </svg>
            {hover && (
                <div
                    className="pointer-events-none fixed z-50 rounded-md bg-slate-900/95 px-3 py-2 text-xs text-white shadow-lg"
                    style={{left: hover.clientX + 14, top: hover.clientY + 14}}
                >
                    <div className="flex justify-between gap-6">
                        <span className="text-slate-400">Position</span>
                        <span className="font-mono">{hover.pos} nt</span>
                    </div>
                    <div className="flex justify-between gap-6">
                        <span className="text-slate-400">RFP</span>
                        <span className="font-mono">{Number.isInteger(hover.rfp) ? hover.rfp : hover.rfp.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between gap-6">
                        <span className="text-slate-400">Frame</span>
                        <span className="font-mono" style={{color: FRAME_COLORS[hover.frame]}}>{hover.frame}</span>
                    </div>
                </div>
            )}
            {mapHover && (
                <div
                    className="pointer-events-none fixed z-50 rounded-md bg-slate-900/95 px-3 py-2 text-xs text-white shadow-lg"
                    style={{left: mapHover.clientX + 14, top: mapHover.clientY + 14}}
                >
                    <div className="flex justify-between gap-6">
                        <span className="text-slate-400">Position</span>
                        <span className="font-mono">{mapHover.pos} nt</span>
                    </div>
                    <div className="flex justify-between gap-6">
                        <span className="text-slate-400">Nucleotide</span>
                        <span className="font-mono">{mapHover.nt}</span>
                    </div>
                    <div className="flex justify-between gap-6">
                        <span className="text-slate-400">Mappability</span>
                        <span className="font-mono">{Number.isFinite(mapHover.value) ? mapHover.value.toFixed(3) : "no data"}</span>
                    </div>
                </div>
            )}
        </>
    );
};

// ---------------------------------------------------------------------------
// Splicing debug modal
// ---------------------------------------------------------------------------

// Sum of exon lengths for a parsed segments array.
const splicedLength = segs => (segs ? segs.reduce((n, [s, e]) => n + (e - s), 0) : null);

// Inclusive spliced length: exon segments [s, e] are inclusive genomic ranges,
// so the true spliced transcript length is Σ (e - s + 1). This equals rna_seq;
// the difference from the half-open Σ (e - s) is exactly the exon count.
const splicedLengthInclusive = segs => (segs ? segs.reduce((n, [s, e]) => n + (e - s + 1), 0) : null);

// ORF transcript coordinates derived from the genomic prot_segments mapping,
// mirroring the plot's fallback path.
const orfRangeFromSegments = (exons, orfSegs, strand) => {
    if (!exons || !orfSegs) return null;
    const coords = [];
    for (const [s, e] of orfSegs) {
        const a = genomicToTranscript(s, exons, strand);
        const b = genomicToTranscript(e - 1, exons, strand);
        if (a !== null) coords.push(a);
        if (b !== null) coords.push(b);
    }
    return coords.length > 0 ? [Math.min(...coords), Math.max(...coords) + 1] : null;
};

// Debug modal: translate the transcript sequence in all six reading frames
// (both orientations) and check whether the exact protein sequence is
// recovered — a direct test of the transcript splicing/orientation. Also
// reports the transcript_segments-derived spliced length and how it disagrees
// with rna_seq (a known data inconsistency that affects coverage alignment).
const SplicingDebug = ({transcriptSequence, proteinSequence, strand, transcriptLabel, orfLabel, transcriptSegments, protSegments, coverage, locationChr, coverageStrand, bigwigFile}) => {
    const seq = normalizeSeq(transcriptSequence);
    const protein = normalizeSeq(proteinSequence);

    if (!seq || !protein) {
        return <p className="text-sm text-slate-600">
            Missing {!seq ? "transcript sequence" : "protein sequence"} for this selection.
        </p>;
    }

    const rc = reverseComplement(seq);
    const orientations = [
        {label: "forward (as stored)", nt: seq},
        {label: "reverse-complement", nt: rc}
    ];

    const results = [];
    for (const o of orientations) {
        for (let f = 0; f < 3; f++) {
            const peptide = translateFrame(o.nt, f);
            const hit = findProtein(peptide, protein);
            results.push({orientation: o.label, frame: f, peptide, hit});
        }
    }
    const anyHit = results.find(r => r.hit);

    // Transcript-segments vs rna_seq comparison.
    const exons = parseSegments(transcriptSegments);
    const orfSegs = parseSegments(protSegments);
    const nExons = exons ? exons.length : null;
    const splicedLen = splicedLength(exons);
    const splicedLenIncl = splicedLengthInclusive(exons);
    const covLen = Array.isArray(coverage) ? coverage.length : null;
    const seqLen = seq.length;
    const discrepancy = splicedLen !== null ? seqLen - splicedLen : null;
    const covMatchesSpliced = covLen !== null && splicedLenIncl !== null && covLen === splicedLenIncl;

    const orfSeg = orfRangeFromSegments(exons, orfSegs, strand);
    const orfSeqRange = findOrfInSequence(seq, protein);
    const segOrfLen = orfSeg ? orfSeg[1] - orfSeg[0] : null;
    const seqOrfLen = orfSeqRange ? orfSeqRange[1] - orfSeqRange[0] : null;

    const row = (label, value, warn) => (
        <div className="flex justify-between gap-6">
            <span className="text-slate-500">{label}</span>
            <span className={warn ? "font-semibold text-amber-700" : "font-mono"}>{value}</span>
        </div>
    );

    return (
        <div className="text-sm text-slate-700" style={{minWidth: "640px"}}>
            <div className="mb-3 space-y-1">
                <div><span className="font-semibold">Transcript:</span> {transcriptLabel || "—"} ({seqLen} nt)</div>
                <div><span className="font-semibold">Protein:</span> {orfLabel || "—"} ({protein.length} aa)</div>
                <div><span className="font-semibold">Reported strand:</span> {strand || "—"}</div>
            </div>

            <div className="mb-3 rounded bg-slate-50 p-2">
                <div className="mb-1 font-semibold">Coverage extraction (bigwig)</div>
                {row("location_chr (used in bw.values)", locationChr ?? "—")}
                {row("strand (used in bw.values)", coverageStrand ?? "—")}
                {row("bigwig file", bigwigFile ?? "—")}
            </div>

            <div className={`mb-3 rounded p-2 ${anyHit ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`}>
                {anyHit
                    ? `✓ Protein recovered in ${anyHit.orientation}, frame ${anyHit.frame} (${anyHit.hit.mode}, aa offset ${anyHit.hit.aaOffset}). Splicing/orientation looks correct.`
                    : "✗ Protein not found in any of the six reading frames. Splicing or the stored transcript sequence is likely wrong."}
            </div>

            <table className="mb-4 w-full border-collapse">
                <thead>
                    <tr className="border-b border-slate-200 text-left text-slate-500">
                        <th className="py-1 pr-3 font-medium">Orientation</th>
                        <th className="py-1 pr-3 font-medium">Frame</th>
                        <th className="py-1 pr-3 font-medium">Match</th>
                        <th className="py-1 pr-3 font-medium">nt offset</th>
                    </tr>
                </thead>
                <tbody>
                    {results.map((r, i) => (
                        <tr key={i} className={`border-b border-slate-100 ${r.hit ? "bg-green-50" : ""}`}>
                            <td className="py-1 pr-3">{r.orientation}</td>
                            <td className="py-1 pr-3">{r.frame}</td>
                            <td className="py-1 pr-3">{r.hit ? `yes (${r.hit.mode})` : "no"}</td>
                            <td className="py-1 pr-3">{r.hit ? r.frame + r.hit.aaOffset * 3 : "—"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div className="mb-2 font-semibold">Coverage length vs spliced transcript (from segments)</div>
            {covLen !== null && splicedLenIncl !== null && (
                <div className={`mb-3 rounded p-2 ${covMatchesSpliced ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`}>
                    {covMatchesSpliced
                        ? `✓ Coverage length (${covLen}) matches the spliced transcript length from segments (Σ(e−s+1) = ${splicedLenIncl}).`
                        : `✗ Coverage length (${covLen}) does NOT match the spliced transcript length from segments (Σ(e−s+1) = ${splicedLenIncl}); difference ${covLen - splicedLenIncl} nt.`}
                </div>
            )}
            <div className="mb-3 space-y-1 rounded bg-slate-50 p-2">
                {row("Exon count (transcript_segments)", nExons ?? "—")}
                {row("Coverage array length (nt)", covLen ?? "—")}
                {row("Spliced length Σ(e−s+1), inclusive (nt)", splicedLenIncl ?? "—")}
                {row("coverage vs Σ(e−s+1)", covMatchesSpliced ? "match" : "MISMATCH", !covMatchesSpliced)}
                {row("rna_seq length (nt)", seqLen)}
                {row("rna_seq vs Σ(e−s+1)", splicedLenIncl !== null && splicedLenIncl === seqLen ? "match" : "MISMATCH", !(splicedLenIncl !== null && splicedLenIncl === seqLen))}
                {row("Σ(e−s), half-open (old server)", splicedLen ?? "—")}
                {row("rna_seq − Σ(e−s)  (= exon count)", discrepancy ?? "—", discrepancy !== null && discrepancy !== 0)}
            </div>

            <div className="mb-2 font-semibold">ORF position: segments vs rna_seq</div>
            <div className="mb-3 space-y-1 rounded bg-slate-50 p-2">
                {row("ORF from segments (nt)", orfSeg ? `${orfSeg[0]}–${orfSeg[1]} (len ${segOrfLen})` : "—")}
                {row("  segments ORF length ÷ 3", segOrfLen !== null ? (segOrfLen % 3 === 0 ? `${segOrfLen / 3} codons` : `${(segOrfLen / 3).toFixed(2)} — NOT a whole codon count`) : "—", segOrfLen !== null && segOrfLen % 3 !== 0)}
                {row("ORF from rna_seq (nt)", orfSeqRange ? `${orfSeqRange[0]}–${orfSeqRange[1]} (len ${seqOrfLen})` : "—")}
                {row("  rna_seq ORF length ÷ 3", seqOrfLen !== null ? (seqOrfLen % 3 === 0 ? `${seqOrfLen / 3} codons` : `${(seqOrfLen / 3).toFixed(2)}`) : "—", seqOrfLen !== null && seqOrfLen % 3 !== 0)}
                {orfSeg && orfSeqRange &&
                    row("start / end offset (rna_seq − segments)", `${orfSeqRange[0] - orfSeg[0]} / ${orfSeqRange[1] - orfSeg[1]}`, (orfSeqRange[0] - orfSeg[0]) !== (orfSeqRange[1] - orfSeg[1]))}
            </div>
            {discrepancy !== null && discrepancy !== 0 && (
                <p className="mb-3 rounded bg-amber-50 p-2 text-xs text-amber-800">
                    rna_seq and transcript_segments describe transcripts of different length. The ORF box and codon
                    marks are anchored to rna_seq (authoritative), but the coverage/exon boxes are rebuilt from the
                    segments, so they can drift by up to this many nt on the shared x-axis.
                </p>
            )}

            <div className="mt-3">
                <div className="font-semibold">Protein sequence ({protein.length} aa)</div>
                <pre className="mt-1 whitespace-pre-wrap break-all rounded bg-slate-50 p-2 font-mono text-xs">{protein}</pre>
            </div>

            {anyHit && (
                <div className="mt-3">
                    <div className="font-semibold">Best-frame translation ({anyHit.orientation}, frame {anyHit.frame})</div>
                    <pre className="mt-1 whitespace-pre-wrap break-all rounded bg-slate-50 p-2 font-mono text-xs">{anyHit.peptide}</pre>
                </div>
            )}
        </div>
    );
};

// Ribocrypt viewer.
// Consumes /proteins/:id/ribocrypt (see routes.py :: ribocrypt) which returns
// one row per (transcript_translation, transcript) pairing with quality
// scores (PIF, UNI, DROPOFF, Measured_TE, RiboNN_TE). Selecting a row loads
// per-ORF ribo-seq coverage from /proteins/:tt_id/ribocrypt_details (see
// routes.py :: ribocrypt_details) — { coverage, transcript_segments,
// prot_segments, transcript_sequence } — and renders it as a zoomable/pannable
// RFP coverage area chart with a transcript/ORF track and a three-frame codon
// track below (RibocryptCoveragePlot), plus the score table.
const RibocryptViewer = ({species_sn, protein_seq_id}) => {
    const {isReady, rows} = useRibocrypt({species_sn, protein_seq_id});

    const [selectedTtId, setSelectedTtId] = useState(null);
    const [showDebug, setShowDebug] = useState(false);

    useEffect(() => {
        if (isReady && rows.length > 0 && selectedTtId === null) {
            setSelectedTtId(rows[0].tt_id);
        }
    }, [isReady, rows, selectedTtId]);

    const {isReady: detailsReady, coverage, mappability, translons, transcriptSegments, protSegments, transcriptSequence, locationChr, strand: detailsStrand, bigwigFile} = useRibocryptDetails({
        species_sn,
        transcript_translation_id: selectedTtId
    });

    const {data: protDetails} = useFetchProtDetails({species_sn, protein_seq_id});
    const proteinSequence = Array.isArray(protDetails) && protDetails.length > 0 ? protDetails[0].sequence : null;

    const selectedRow = rows.find(r => r.tt_id === selectedTtId);

    return (
        <div className="shadow-[0px_0px_6px_0px_rgba(0,0,0,0.2)] rounded p-3 mt-3 mr-3">
            <div className="border-b width-full border-slate-200">
                <HeaderWithModal title="Riboseq">
                    <p>Riboseq quality scores and translation efficiency metrics for each ORF on this protein's transcripts.</p>
                </HeaderWithModal>
            </div>

            {!isReady ? (
                <div className="mt-4 text-sm text-slate-500">Loading…</div>
            ) : rows.length === 0 ? (
                <div className="mt-4 flex h-64 items-center justify-center rounded border border-dashed border-slate-300 bg-slate-50 text-slate-500">
                    <div className="text-sm">No Riboseq data available for this protein.</div>
                </div>
            ) : (
                <>
                    <div className="mt-4 max-h-40 overflow-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-200 text-left text-slate-500">
                                    <th className="py-1 pr-3 font-medium">Transcript</th>
                                    <th className="py-1 pr-3 font-medium">Quality</th>
                                    <th className="py-1 pr-3 font-medium">PIF</th>
                                    <th className="py-1 pr-3 font-medium">UNI</th>
                                    <th className="py-1 pr-3 font-medium">DROPOFF</th>
                                    <th className="py-1 pr-3 font-medium">Measured TE</th>
                                    <th className="py-1 pr-3 font-medium">RiboNN TE</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => (
                                    <tr
                                        key={`${r.tt_id}-${r.transcript_id}-${i}`}
                                        onClick={() => setSelectedTtId(r.tt_id)}
                                        className={`cursor-pointer border-b border-slate-100 last:border-0 hover:bg-slate-50 ${selectedTtId === r.tt_id ? 'bg-slate-100' : ''}`}
                                    >
                                        <td className="py-1 pr-3 font-mono">{r.trans_accession}</td>
                                        <td className="py-1 pr-3">{r.quality_score}</td>
                                        <td className="py-1 pr-3">{formatNum(r.PIF)}</td>
                                        <td className="py-1 pr-3">{formatNum(r.UNI)}</td>
                                        <td className="py-1 pr-3">{formatNum(r.DROPOFF)}</td>
                                        <td className="py-1 pr-3">{formatNum(r.Measured_TE)}</td>
                                        <td className="py-1 pr-3">{formatNum(r.RiboNN_TE)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {selectedTtId === null ? (
                        <div className="mt-4 flex h-48 items-center justify-center rounded border border-dashed border-slate-300 bg-slate-50 text-slate-500">
                            <div className="text-sm">Select a transcript above to load its RFP coverage.</div>
                        </div>
                    ) : !detailsReady ? (
                        <div className="mt-4 flex h-48 items-center justify-center rounded border border-dashed border-slate-300 bg-slate-50 text-slate-500">
                            <div className="text-sm">Loading coverage for tt_id {selectedTtId}…</div>
                        </div>
                    ) : coverage === null || coverage.length === 0 ? (
                        <div className="mt-4 flex h-48 items-center justify-center rounded border border-dashed border-slate-300 bg-slate-50 text-slate-500">
                            <div className="text-sm">No coverage data available for tt_id {selectedTtId}.</div>
                        </div>
                    ) : (
                        <div>
                            {SHOW_DEBUG_MODAL && (
                                <div className="mt-2 flex justify-end">
                                    <button
                                        onClick={() => setShowDebug(true)}
                                        className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50"
                                    >
                                        Debug splicing
                                    </button>
                                </div>
                            )}
                            <RibocryptCoveragePlot
                                key={selectedTtId}
                                coverage={coverage}
                                mappability={mappability}
                                translons={translons}
                                transcriptSegments={transcriptSegments}
                                protSegments={protSegments}
                                strand={selectedRow ? selectedRow.strand : null}
                                transcriptLabel={selectedRow ? selectedRow.trans_accession : null}
                                orfLabel={selectedRow ? selectedRow.prot_acc : null}
                                transcriptSequence={transcriptSequence}
                                proteinSequence={proteinSequence}
                            />
                        </div>
                    )}

                    {SHOW_DEBUG_MODAL && showDebug && (
                        <Modal title="Splicing debug — translate transcript" onClose={() => setShowDebug(false)}>
                            <SplicingDebug
                                transcriptSequence={transcriptSequence}
                                proteinSequence={proteinSequence}
                                strand={selectedRow ? selectedRow.strand : null}
                                transcriptLabel={selectedRow ? selectedRow.trans_accession : null}
                                orfLabel={selectedRow ? selectedRow.prot_acc : null}
                                transcriptSegments={transcriptSegments}
                                protSegments={protSegments}
                                coverage={coverage}
                                locationChr={locationChr}
                                coverageStrand={detailsStrand}
                                bigwigFile={bigwigFile}
                            />
                        </Modal>
                    )}
                </>
            )}
        </div>
    );
};

const formatNum = v => {
    if (v === null || v === undefined || v === '') return '—';
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(3) : String(v);
};

export default RibocryptViewer;
